<template>
  <div
    v-if="visible"
    class="evolucao-overlay"
    @click.self="fechar"
  >
    <div class="evolucao-panel">
      <div class="evolucao-header">
        <div class="evolucao-header-text">
          <h3>{{ tituloMunicipio }}</h3>
          <p class="meta">
            <span
              v-if="badgeEquipe"
              class="tipo-equipe-badge"
            >{{ badgeEquipe }}</span>
            <span class="meta-titulo">{{ tituloEvolucao }}</span>
          </p>
        </div>

        <button type="button" aria-label="Fechar" @click="fechar">
          <i class="icofont-close"></i>
        </button>
      </div>

      <div class="evolucao-body">
        <div v-if="loading" class="evolucao-state">
          <i class="icofont-spinner-alt-3 spin"></i>
          <strong>Carregando histórico…</strong>
          <span>Buscando a evolução no município.</span>
        </div>

        <div v-else-if="erro" class="evolucao-state">
          <i class="icofont-warning"></i>
          <strong>Não foi possível carregar o histórico</strong>
          <span>{{ erro }}</span>
          <button type="button" class="btn-retry" @click="carregarHistorico">
            Tentar novamente
          </button>
        </div>

        <div v-else-if="!serieCompleta.length" class="evolucao-state">
          <i class="icofont-chart-line-alt"></i>
          <strong>Sem histórico para {{ tituloEvolucao }}</strong>
          <span>
            Não há registros de Regular, Suficiente, Boa ou Ótima ao longo dos
            quadrimestres
            <template v-if="modo === 'indicador'">
              para {{ tpEq }} · {{ dsInd }}
            </template>
            <template v-else> para este componente </template>.
          </span>
        </div>

        <template v-else>
          <div class="filtros-periodo">
            <div
              v-for="grupo in filtrosPorAno"
              :key="grupo.ano"
              class="filtro-ano"
            >
              <button
                type="button"
                class="filtro-ano-toggle"
                :class="{ active: anoTotalmenteSelecionado(grupo.ano) }"
                :title="`Selecionar ou limpar ${grupo.ano}`"
                @click="toggleAnoCompleto(grupo.ano)"
              >
                {{ grupo.ano }}
              </button>
              <div class="filtro-opcoes">
                <button
                  v-for="q in grupo.qs"
                  :key="`${grupo.ano}-Q${q}`"
                  type="button"
                  class="filtro-chip"
                  :class="{
                    active: periodosSelecionados.includes(`${grupo.ano}Q${q}`),
                  }"
                  @click="togglePeriodo(grupo.ano, q)"
                >
                  Q{{ q }}
                </button>
              </div>
            </div>
          </div>

          <div v-if="!serieVisivel.length" class="evolucao-state compact">
            <strong>Nenhum período na janela filtrada</strong>
            <span>Selecione ao menos um quadrimestre nos filtros acima.</span>
          </div>

          <template v-else>
            <div
              class="chart-wrap"
              :class="{ 'is-q-hover': chartHoverNoQ }"
              @mousemove="onChartMouseMove"
              @mouseleave="onChartMouseLeave"
            >
              <apexchart
                v-if="chartPronto"
                ref="evolucaoChart"
                type="line"
                height="320"
                :options="chartOptionsState"
                :series="chartSeriesState"
              />
            </div>

            <div v-if="periodoSelecionado" class="period-box">
              <div class="stats">
                <div
                  v-for="(item, index) in classificacoes"
                  :key="`stat-${item.key}`"
                  class="stat"
                  :class="[item.key, situacaoFaixa(periodoSelecionado, index)]"
                >
                  <span class="cat">{{ item.label }}</span>
                  <span
                    class="legend-swatch"
                    :class="`shape-${item.shape}`"
                    aria-hidden="true"
                  ></span>
                  <div class="stat-body">
                    <span
                      v-if="
                        situacaoFaixa(periodoSelecionado, index) === 'check'
                      "
                      class="stat-check"
                      aria-label="Faixa já superada"
                    >
                      <i class="icofont-check"></i>
                    </span>
                    <template
                      v-else-if="
                        situacaoFaixa(periodoSelecionado, index) ===
                        'contagem'
                      "
                    >
                      <span class="qty">{{
                        formatNumber(periodoSelecionado[item.key])
                      }}</span>
                      <span class="unit">{{
                        Number(periodoSelecionado[item.key]) === 1
                          ? 'equipe'
                          : 'equipes'
                      }}</span>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

const CORES = {
  reg: '#c2410c',
  suf: '#a16207',
  bom: '#15803d',
  oti: '#1d4ed8',
}

export default {
  name: 'ModalEvolucaoIndicador',
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    ibge: {
      type: [String, Number],
      default: '',
    },
    municipioNome: {
      type: String,
      default: '',
    },
    uf: {
      type: String,
      default: '',
    },
    tpEq: {
      type: String,
      default: '',
    },
    dsInd: {
      type: String,
      default: '',
    },
    quadrimestreAtual: {
      type: String,
      default: '',
    },
    /** 'indicador' | 'componente' */
    modo: {
      type: String,
      default: 'indicador',
    },
    /** 'cvat' | 'qualidade' — usado quando modo === 'componente' */
    componente: {
      type: String,
      default: '',
    },
    /** Tipos de equipe ativos no filtro de Componentes (eSF, eAP, …). */
    tiposEquipe: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      erro: '',
      requestId: 0,
      snapshots: [],
      historicoCache: null,
      periodosSelecionados: [],
      periodoSelecionadoKey: '',
      chartPronto: false,
      chartOptionsState: {},
      chartSeriesState: [],
      chartHoverNoQ: false,
      classificacoes: [
        { key: 'reg', label: 'Regulares', shape: 'circle' },
        { key: 'suf', label: 'Suficientes', shape: 'square' },
        { key: 'bom', label: 'Boas', shape: 'triangle' },
        { key: 'oti', label: 'Ótimas', shape: 'diamond' },
      ],
    }
  },
  computed: {
    tituloMunicipio() {
      const nome = this.municipioNome || '—'
      return this.uf ? `${nome} - ${this.uf}` : nome
    },

    tituloEvolucao() {
      if (this.modo === 'componente') {
        return this.componente === 'qualidade'
          ? 'Componente de Qualidade (CQ)'
          : 'Componente de Vínculo e Acompanhamento Territorial (CVAT)'
      }

      return this.dsInd || 'Indicador'
    },

    /** Badge do tipo de equipe — só no modo indicador. */
    badgeEquipe() {
      if (this.modo === 'indicador') {
        return this.tpEq || ''
      }

      return ''
    },

    serieCompleta() {
      if (this.modo === 'componente') {
        return this.extrairSerieComponente(
          this.snapshots,
          this.componente,
          this.tiposEquipe
        )
      }

      return this.extrairSerieIndicador(this.snapshots, this.tpEq, this.dsInd)
    },

    filtrosPorAno() {
      const mapa = new Map()

      this.serieCompleta.forEach((item) => {
        const parsed = this.parsePeriodo(item.quadrimestre)

        if (!parsed) {
          return
        }

        if (!mapa.has(parsed.ano)) {
          mapa.set(parsed.ano, new Set())
        }

        mapa.get(parsed.ano).add(parsed.q)
      })

      return [...mapa.entries()]
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .map(([ano, qs]) => ({
          ano,
          qs: [...qs].sort((a, b) => Number(a) - Number(b)),
        }))
    },

    serieVisivel() {
      return this.serieCompleta.filter((item) =>
        this.periodosSelecionados.includes(
          this.normalizarChavePeriodo(item.quadrimestre)
        )
      )
    },

    periodoSelecionado() {
      return (
        this.serieVisivel.find(
          (item) => item.quadrimestre === this.periodoSelecionadoKey
        ) || null
      )
    },

  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          this.carregarHistorico()
        } else {
          this.resetEstado()
        }
      },
    },
    // Só refetch se o modal já estava aberto e o IBGE mudou.
    // Na abertura, contexto+visible mudam juntos e o visible já carrega.
    ibge(value, oldValue) {
      if (this.visible && oldValue && value && value !== oldValue) {
        this.carregarHistorico()
      }
    },
    tpEq() {
      this.reativarSerieLocal()
    },
    dsInd() {
      this.reativarSerieLocal()
    },
    modo() {
      this.reativarSerieLocal()
    },
    componente() {
      this.reativarSerieLocal()
    },
    tiposEquipe: {
      deep: true,
      handler() {
        this.reativarSerieLocal()
      },
    },
    serieVisivel: {
      handler() {
        this.garantirPeriodoSelecionado()
        this.montarGrafico()
      },
    },
    periodoSelecionadoKey() {
      this.atualizarDestaqueSelecao()
    },
  },
  methods: {
    fechar() {
      this.$emit('close')
    },

    /**
     * Troca de indicador/componente/tipos: reusa snapshots e remonta o gráfico.
     */
    reativarSerieLocal() {
      if (!this.visible || !this.snapshots.length) {
        return
      }

      this.inicializarFiltros()
      this.garantirPeriodoSelecionado()
      this.montarGrafico()
    },

    resetEstado() {
      this.loading = false
      this.erro = ''
      this.snapshots = []
      this.periodosSelecionados = []
      this.periodoSelecionadoKey = ''
      this.chartPronto = false
      this.chartOptionsState = {}
      this.chartSeriesState = []
      this.chartHoverNoQ = false
      // mantém historicoCache — a API é a mesma para todos os indicadores do município
    },

    montarGrafico() {
      const categorias = this.serieVisivel.map((item) =>
        this.formatPeriodoLabel(item.quadrimestre)
      )

      if (!categorias.length) {
        this.chartPronto = false
        this.chartSeriesState = []
        return
      }

      const series = this.classificacoes.map((item) => ({
        name: item.label,
        data: this.serieVisivel.map((ponto) => Number(ponto[item.key] || 0)),
      }))

      const maxY = Math.max(
        1,
        ...series.flatMap((serie) => serie.data.map((value) => Number(value) || 0))
      )

      const labelSelecionado = this.periodoSelecionado
        ? this.formatPeriodoLabel(this.periodoSelecionado.quadrimestre)
        : ''

      this.chartPronto = false
      this.chartSeriesState = series
      this.chartOptionsState = {
        chart: {
          type: 'line',
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
          fontFamily: 'inherit',
          events: {
            click: this.onChartAreaClick,
            markerClick: this.onChartMarkerClick,
            dataPointSelection: this.onChartMarkerClick,
            xAxisLabelClick: this.onChartAxisClick,
            // triangle/diamond não existem no Apex 3.50 — desenhamos depois do render
            mounted: this.aplicarFormasCustomizadas,
            updated: this.aplicarFormasCustomizadas,
          },
        },
        colors: [CORES.reg, CORES.suf, CORES.bom, CORES.oti],
        stroke: {
          curve: 'straight',
          width: 3,
        },
        markers: {
          size: 7,
          strokeWidth: 2,
          strokeColors: '#ffffff',
          // só formas nativas aqui (circle/square); triangle/diamond via SVG custom
          shape: this.classificacoes.map((item) =>
            item.shape === 'square' ? 'square' : 'circle'
          ),
          hover: {
            size: 8,
          },
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        grid: {
          borderColor: '#e7eff1',
          strokeDashArray: 4,
          padding: { left: 8, right: 12 },
        },
        annotations: this.buildAnnotationsSelecao(categorias, labelSelecionado),
        xaxis: {
          categories: categorias,
          tooltip: { enabled: false },
          crosshairs: { show: false },
          labels: {
            style: {
              colors: categorias.map((label) =>
                label === labelSelecionado ? '#0f172a' : '#607782'
              ),
              fontSize: '12px',
              fontWeight: categorias.map((label) =>
                label === labelSelecionado ? 700 : 500
              ),
            },
          },
          axisBorder: { color: '#aabfc6' },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          max: maxY,
          forceNiceScale: true,
          decimalsInFloat: 0,
          title: {
            text: 'EQUIPES POR CLASSIFICAÇÃO',
            style: {
              fontSize: '11px',
              fontWeight: 700,
              color: '#3e5e6b',
            },
          },
          labels: {
            formatter: (value) =>
              this.formatNumber(Math.round(Number(value) || 0)),
            style: { colors: '#607782', fontSize: '12px' },
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: ({ dataPointIndex, w }) =>
            this.buildTooltipHtml(dataPointIndex, w),
        },
      }

      this.$nextTick(() => {
        this.chartPronto = true
      })
    },

    /**
     * Linha tracejada vertical do período selecionado.
     *
     * @param {string[]} [categorias]
     * @param {string} [labelSelecionado]
     * @returns {{ xaxis: object[] }}
     */
    buildAnnotationsSelecao(categorias, labelSelecionado) {
      const cats =
        categorias ||
        this.serieVisivel.map((item) =>
          this.formatPeriodoLabel(item.quadrimestre)
        )
      const label =
        labelSelecionado ||
        (this.periodoSelecionado
          ? this.formatPeriodoLabel(this.periodoSelecionado.quadrimestre)
          : '')

      if (!label || !cats.includes(label)) {
        return { xaxis: [] }
      }

      return {
        xaxis: [
          {
            x: label,
            borderColor: '#94a3b8',
            strokeDashArray: 4,
            borderWidth: 1,
            label: {
              text: '',
              style: {
                color: 'transparent',
                fontSize: '0px',
              },
            },
          },
        ],
      }
    },

    /**
     * Atualiza só o destaque do período, sem remontar o gráfico.
     */
    atualizarDestaqueSelecao() {
      if (!this.chartPronto) {
        return
      }

      const chart =
        this.$refs.evolucaoChart && this.$refs.evolucaoChart.chart

      if (!chart) {
        return
      }

      const categorias = this.serieVisivel.map((item) =>
        this.formatPeriodoLabel(item.quadrimestre)
      )
      const labelSelecionado = this.periodoSelecionado
        ? this.formatPeriodoLabel(this.periodoSelecionado.quadrimestre)
        : ''

      chart.updateOptions(
        {
          annotations: this.buildAnnotationsSelecao(
            categorias,
            labelSelecionado
          ),
          xaxis: {
            categories: categorias,
            labels: {
              style: {
                colors: categorias.map((label) =>
                  label === labelSelecionado ? '#0f172a' : '#607782'
                ),
                fontSize: '12px',
                fontWeight: categorias.map((label) =>
                  label === labelSelecionado ? 700 : 500
                ),
              },
            },
          },
        },
        false,
        false,
        false
      )

      this.$nextTick(() => {
        this.aplicarFormasCustomizadas({ el: chart.el, w: chart.w })
      })
    },

    /**
     * HTML do tooltip com as mesmas formas da legenda/gráfico.
     *
     * @param {number} dataPointIndex Índice do período.
     * @param {object} w Estado interno do ApexCharts.
     * @returns {string}
     */
    buildTooltipHtml(dataPointIndex, w) {
      const titulo =
        (w.globals.categoryLabels && w.globals.categoryLabels[dataPointIndex]) ||
        (w.globals.labels && w.globals.labels[dataPointIndex]) ||
        ''

      const linhas = (w.config.series || [])
        .map((serie, seriesIndex) => {
          const item = this.classificacoes[seriesIndex]
          const cor =
            (w.config.colors && w.config.colors[seriesIndex]) ||
            CORES[item && item.key] ||
            '#64748b'
          const valor = Array.isArray(serie.data)
            ? serie.data[dataPointIndex]
            : null
          const texto =
            valor == null
              ? '—'
              : `${this.formatNumber(Number(valor) || 0)} equipes`

          return `
            <div style="display:flex;align-items:center;gap:8px;padding:3px 0;font-size:12px;color:#334155;">
              ${this.tooltipMarkerHtml(item && item.shape, cor)}
              <span style="flex:1;">${serie.name || (item && item.label) || ''}</span>
              <strong style="font-variant-numeric:tabular-nums;">${texto}</strong>
            </div>
          `
        })
        .join('')

      return `
        <div style="background:#fff;border:1px solid #dbe7eb;border-radius:10px;box-shadow:0 8px 24px rgba(15,55,70,.12);padding:10px 12px;min-width:180px;">
          <div style="font-size:11px;font-weight:700;color:#3e5e6b;margin-bottom:6px;letter-spacing:.02em;">
            ${titulo}
          </div>
          ${linhas}
        </div>
      `
    },

    /**
     * Marker inline do tooltip (círculo/quadrado/triângulo/losango).
     *
     * @param {string} shape Forma.
     * @param {string} color Cor.
     * @returns {string}
     */
    tooltipMarkerHtml(shape, color) {
      if (shape === 'triangle') {
        return `<span style="display:inline-block;width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:9px solid ${color};flex:none;"></span>`
      }

      if (shape === 'diamond') {
        return `<span style="display:inline-block;width:8px;height:8px;background:${color};transform:rotate(45deg);margin:0 2px;flex:none;"></span>`
      }

      if (shape === 'square') {
        return `<span style="display:inline-block;width:9px;height:9px;background:${color};border-radius:1px;flex:none;"></span>`
      }

      return `<span style="display:inline-block;width:9px;height:9px;background:${color};border-radius:50%;flex:none;"></span>`
    },

    /**
     * Substitui markers genéricos por triângulo/losango (Apex 3.50 não tem nativo).
     *
     * @param {object} chartContext Contexto do ApexCharts.
     */
    aplicarFormasCustomizadas(chartContext) {
      const root =
        (chartContext && chartContext.el) ||
        (chartContext &&
          chartContext.w &&
          chartContext.w.globals &&
          chartContext.w.globals.dom &&
          chartContext.w.globals.dom.baseEl)

      if (!root) {
        return
      }

      const cores = [CORES.reg, CORES.suf, CORES.bom, CORES.oti]
      const markers = root.querySelectorAll(
        '.apexcharts-series-markers .apexcharts-marker'
      )

      markers.forEach((marker) => {
        const seriesIndex = Number(marker.getAttribute('index'))
        const shape =
          this.classificacoes[seriesIndex] &&
          this.classificacoes[seriesIndex].shape

        if (shape !== 'triangle' && shape !== 'diamond') {
          return
        }

        if (marker.getAttribute('data-evolucao-shape') === shape) {
          return
        }

        const cx = parseFloat(marker.getAttribute('cx'))
        const cy = parseFloat(marker.getAttribute('cy'))
        const size = parseFloat(
          marker.getAttribute('r') ||
            marker.getAttribute('default-marker-size') ||
            7
        )

        if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
          return
        }

        const path = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'path'
        )

        ;[
          'index',
          'j',
          'rel',
          'default-marker-size',
          'class',
        ].forEach((attr) => {
          if (marker.hasAttribute(attr)) {
            path.setAttribute(attr, marker.getAttribute(attr))
          }
        })

        path.setAttribute('cx', String(cx))
        path.setAttribute('cy', String(cy))
        path.setAttribute('fill', cores[seriesIndex])
        path.setAttribute(
          'stroke',
          marker.getAttribute('stroke') || '#ffffff'
        )
        path.setAttribute(
          'stroke-width',
          marker.getAttribute('stroke-width') || '2'
        )
        path.setAttribute('d', this.pathMarcador(shape, cx, cy, size))
        path.setAttribute('data-evolucao-shape', shape)

        if (marker.parentNode) {
          marker.parentNode.replaceChild(path, marker)
        }
      })
    },

    /**
     * Path SVG para triângulo ou losango centrado em (cx, cy).
     *
     * @param {'triangle'|'diamond'} shape Forma.
     * @param {number} cx Centro X.
     * @param {number} cy Centro Y.
     * @param {number} size Raio aproximado.
     * @returns {string}
     */
    pathMarcador(shape, cx, cy, size) {
      if (shape === 'triangle') {
        const h = size * 1.15
        return `M ${cx} ${cy - h} L ${cx + h} ${cy + h * 0.75} L ${
          cx - h
        } ${cy + h * 0.75} Z`
      }

      const s = size * 1.1
      return `M ${cx} ${cy - s} L ${cx + s} ${cy} L ${cx} ${cy + s} L ${
        cx - s
      } ${cy} Z`
    },

    /**
     * Normaliza o IBGE para a rota de histórico.
     *
     * @param {string|number} ibge Código IBGE.
     * @returns {string}
     */
    toCoIbge(ibge) {
      return String(ibge || '').replace(/\D/g, '')
    },

    /**
     * Faz parse de campos que a API devolve como JSON stringificado.
     *
     * @param {*} value Valor bruto.
     * @returns {*}
     */
    parseJsonField(value) {
      if (value == null) {
        return null
      }

      if (typeof value === 'object') {
        return value
      }

      if (typeof value !== 'string') {
        return value
      }

      const trimmed = value.trim()

      if (!trimmed || trimmed === 'null') {
        return null
      }

      try {
        return JSON.parse(trimmed)
      } catch (error) {
        console.error('[ModalEvolucaoIndicador] JSON inválido', error, trimmed)
        return null
      }
    },

    /**
     * Normaliza um item de indicador da API.
     *
     * @param {Object} ind Indicador bruto.
     * @returns {Object}
     */
    normalizarIndicador(ind) {
      return {
        tp_eq: ind.tp_eq || ind.tipo_equipe || ind.tipo || '',
        ds_ind: ind.ds_ind || ind.indicador || ind.nome_indicador || '',
        reg: Number(ind.reg ?? ind.regular ?? 0),
        suf: Number(ind.suf ?? ind.suficiente ?? 0),
        bom: Number(ind.bom ?? 0),
        oti: Number(ind.oti ?? ind.otimo ?? 0),
        avg: ind.avg != null ? Number(ind.avg) : null,
      }
    },

    /**
     * Normaliza o bloco de classificação (CVAT / Qualidade) de um snapshot.
     *
     * @param {*} raw Valor bruto (objeto ou JSON string).
     * @returns {Object}
     */
    normalizarClassificacao(raw) {
      const obj = this.parseJsonField(raw) || {}

      return {
        esf_cvat: Number(obj.esf_cvat || 0),
        eap_cvat: Number(obj.eap_cvat || 0),
        esf_qual: Number(obj.esf_qual || 0),
        eap_qual: Number(obj.eap_qual || 0),
        esb_qual: Number(obj.esb_qual || 0),
        emulti_qual: Number(obj.emulti_qual || 0),
        esfr_qual: Number(obj.esfr_qual || 0),
        eapp_qual: Number(obj.eapp_qual || 0),
        ecr_qual: Number(obj.ecr_qual || 0),
      }
    },

    /**
     * Soma as equipes do componente nas tipologias selecionadas.
     *
     * @param {Object} classificacao Bloco regular/suficiente/bom/ótimo.
     * @param {string} componente 'cvat' | 'qualidade'.
     * @param {string[]} tipos Tipos selecionados (eSF, eAP, …).
     * @returns {number}
     */
    totalComponenteClassificacao(classificacao, componente, tipos) {
      const camposCvat = {
        eSF: 'esf_cvat',
        eAP: 'eap_cvat',
      }
      const camposQual = {
        eSF: 'esf_qual',
        eAP: 'eap_qual',
        eSB: 'esb_qual',
        eMulti: 'emulti_qual',
        eSFR: 'esfr_qual',
        eAPP: 'eapp_qual',
        eCR: 'ecr_qual',
      }
      const mapa = componente === 'qualidade' ? camposQual : camposCvat
      const lista =
        Array.isArray(tipos) && tipos.length ? tipos : Object.keys(mapa)

      return lista.reduce((soma, tipo) => {
        const campo = mapa[tipo]
        return soma + (campo ? Number(classificacao[campo] || 0) : 0)
      }, 0)
    },

    /**
     * Normaliza a resposta do endpoint de histórico em snapshots por quadrimestre.
     *
     * A API atual devolve um array sem `quadrimestre`, com `indicadores` (e
     * classificações) como JSON stringificado, em ordem cronológica crescente.
     * Os períodos vêm de `/ranking-geral/quadrimestres` (também crescente).
     *
     * @param {*} payload Resposta da API.
     * @param {string[]} periodosOrdenados Quadrimestres em ordem crescente.
     * @returns {Array}
     */
    normalizarHistorico(payload, periodosOrdenados = []) {
      const raw = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.historico)
            ? payload.historico
            : []

      if (!raw.length) {
        return []
      }

      const first = raw[0] || {}
      const indicadoresPrimeiro = this.parseJsonField(first.indicadores)
      const isFlat =
        first &&
        (first.tp_eq != null || first.ds_ind != null) &&
        !Array.isArray(indicadoresPrimeiro) &&
        typeof first.indicadores !== 'string'

      if (isFlat) {
        const byPeriod = new Map()

        raw.forEach((row) => {
          const quadrimestre = this.normalizarChavePeriodo(
            row.quadrimestre || row.periodo || row.quad
          )

          if (!quadrimestre) {
            return
          }

          if (!byPeriod.has(quadrimestre)) {
            byPeriod.set(quadrimestre, {
              quadrimestre,
              indicadores: [],
              regular: this.normalizarClassificacao(null),
              suficiente: this.normalizarClassificacao(null),
              bom: this.normalizarClassificacao(null),
              otimo: this.normalizarClassificacao(null),
            })
          }

          byPeriod
            .get(quadrimestre)
            .indicadores.push(this.normalizarIndicador(row))
        })

        return this.ordenarSnapshots([...byPeriod.values()])
      }

      const snapshots = raw
        .map((item, index) => {
          const quadrimestre = this.normalizarChavePeriodo(
            item.quadrimestre ||
              item.periodo ||
              item.quad ||
              periodosOrdenados[index]
          )

          if (!quadrimestre) {
            return null
          }

          const indicadoresRaw = this.parseJsonField(item.indicadores)
          const indicadores = Array.isArray(indicadoresRaw)
            ? indicadoresRaw.map((ind) => this.normalizarIndicador(ind))
            : []

          return {
            quadrimestre,
            indicadores,
            regular: this.normalizarClassificacao(
              item.regular ?? item.regulares
            ),
            suficiente: this.normalizarClassificacao(
              item.suficiente ?? item.suficientes
            ),
            bom: this.normalizarClassificacao(item.bom ?? item.bons),
            otimo: this.normalizarClassificacao(
              item.otimo ?? item.otimos ?? item.oti
            ),
          }
        })
        .filter(Boolean)

      return this.ordenarSnapshots(snapshots)
    },

    ordenarSnapshots(snapshots) {
      return [...snapshots].sort((a, b) =>
        this.compararPeriodos(a.quadrimestre, b.quadrimestre)
      )
    },

    /**
     * Extrai a série do indicador/tipo de equipe ao longo dos snapshots.
     *
     * @param {Array} snapshots Snapshots normalizados.
     * @param {string} tpEq Tipo de equipe.
     * @param {string} dsInd Nome do indicador.
     * @returns {Array}
     */
    extrairSerieIndicador(snapshots, tpEq, dsInd) {
      const tipo = String(tpEq || '').trim().toLowerCase()
      const nome = this.normalizarTexto(dsInd)

      return (snapshots || [])
        .map((snap) => {
          const indicador = (snap.indicadores || []).find((item) => {
            const mesmoTipo =
              String(item.tp_eq || '').trim().toLowerCase() === tipo
            const mesmoNome = this.normalizarTexto(item.ds_ind) === nome

            return mesmoTipo && mesmoNome
          })

          if (!indicador) {
            return null
          }

          return {
            quadrimestre: snap.quadrimestre,
            reg: Number(indicador.reg || 0),
            suf: Number(indicador.suf || 0),
            bom: Number(indicador.bom || 0),
            oti: Number(indicador.oti || 0),
          }
        })
        .filter(Boolean)
    },

    /**
     * Extrai a série de CVAT ou Qualidade (Regular→Ótimo) ao longo dos snapshots.
     *
     * @param {Array} snapshots Snapshots normalizados.
     * @param {string} componente 'cvat' | 'qualidade'.
     * @param {string[]} tiposEquipe Tipos filtrados.
     * @returns {Array}
     */
    extrairSerieComponente(snapshots, componente, tiposEquipe) {
      const chave = String(componente || '').toLowerCase()

      if (chave !== 'cvat' && chave !== 'qualidade') {
        return []
      }

      return (snapshots || []).map((snap) => ({
        quadrimestre: snap.quadrimestre,
        reg: this.totalComponenteClassificacao(
          snap.regular || {},
          chave,
          tiposEquipe
        ),
        suf: this.totalComponenteClassificacao(
          snap.suficiente || {},
          chave,
          tiposEquipe
        ),
        bom: this.totalComponenteClassificacao(
          snap.bom || {},
          chave,
          tiposEquipe
        ),
        oti: this.totalComponenteClassificacao(
          snap.otimo || {},
          chave,
          tiposEquipe
        ),
      }))
    },

    normalizarTexto(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
    },

    /**
     * Normaliza chaves de período para o formato canônico `YYYYQn`.
     *
     * @param {string} value Valor bruto.
     * @returns {string}
     */
    normalizarChavePeriodo(value) {
      const raw = String(value || '').trim().toUpperCase()

      if (!raw) {
        return ''
      }

      const matchIso = raw.match(/^(\d{4})\s*Q\s*([1-3])$/)

      if (matchIso) {
        return `${matchIso[1]}Q${matchIso[2]}`
      }

      const matchLabel = raw.match(/^Q\s*([1-3])\s*[/.-]\s*(\d{4})$/)

      if (matchLabel) {
        return `${matchLabel[2]}Q${matchLabel[1]}`
      }

      return raw
    },

    parsePeriodo(value) {
      const key = this.normalizarChavePeriodo(value)
      const match = key.match(/^(\d{4})Q([1-3])$/)

      if (!match) {
        return null
      }

      return { ano: match[1], q: match[2], key }
    },

    formatPeriodoLabel(value) {
      const parsed = this.parsePeriodo(value)

      if (!parsed) {
        return String(value || '—')
      }

      return `Q${parsed.q}/${parsed.ano}`
    },

    compararPeriodos(a, b) {
      const pa = this.parsePeriodo(a)
      const pb = this.parsePeriodo(b)

      if (!pa && !pb) {
        return String(a).localeCompare(String(b))
      }

      if (!pa) {
        return 1
      }

      if (!pb) {
        return -1
      }

      if (pa.ano !== pb.ano) {
        return Number(pa.ano) - Number(pb.ano)
      }

      return Number(pa.q) - Number(pb.q)
    },

    async carregarHistorico() {
      const coIbge = this.toCoIbge(this.ibge)

      if (!coIbge) {
        this.erro = 'IBGE do município não informado.'
        this.snapshots = []
        return
      }

      if (
        this.historicoCache &&
        this.historicoCache.ibge === coIbge &&
        this.historicoCache.payload != null
      ) {
        this.erro = ''
        this.loading = false
        this.snapshots = this.normalizarHistorico(
          this.historicoCache.payload,
          this.historicoCache.periodosOrdenados || []
        )
        this.inicializarFiltros()
        this.garantirPeriodoSelecionado()
        return
      }

      const currentRequest = ++this.requestId

      this.loading = true
      this.erro = ''

      try {
        const [historicoResponse, quadrimestresResponse] = await Promise.all([
          this.$apiPublic.get(
            `/cofinanciamento-federal-aps/ranking-geral/${coIbge}/historico`,
            { returnReject: true }
          ),
          this.$apiPublic.get(
            '/cofinanciamento-federal-aps/ranking-geral/quadrimestres',
            { returnReject: true }
          ),
        ])

        if (currentRequest !== this.requestId) {
          return
        }

        const periodosOrdenados = (quadrimestresResponse.data?.data || [])
          .map((item) => this.normalizarChavePeriodo(item.quadrimestre))
          .filter(Boolean)
          .sort((a, b) => this.compararPeriodos(a, b))

        this.snapshots = this.normalizarHistorico(
          historicoResponse.data,
          periodosOrdenados
        )
        this.historicoCache = {
          ibge: coIbge,
          payload: historicoResponse.data,
          periodosOrdenados,
        }
        this.inicializarFiltros()
        this.garantirPeriodoSelecionado()
      } catch (error) {
        console.error(
          '[ModalEvolucaoIndicador] historico',
          `/cofinanciamento-federal-aps/ranking-geral/${coIbge}/historico`,
          error?.response?.status,
          error?.response?.data || error
        )

        if (currentRequest !== this.requestId) {
          return
        }

        this.snapshots = []
        this.historicoCache = null
        this.erro =
          error?.response?.data?.message ||
          error?.response?.data?.co_ibge?.[0] ||
          'Falha ao consultar o histórico do município.'
      } finally {
        if (currentRequest === this.requestId) {
          this.loading = false
        }
      }
    },

    inicializarFiltros() {
      this.periodosSelecionados = this.serieCompleta
        .map((item) => this.normalizarChavePeriodo(item.quadrimestre))
        .filter(Boolean)
    },

    periodosDoAno(ano) {
      const grupo = this.filtrosPorAno.find((item) => item.ano === ano)

      if (!grupo) {
        return []
      }

      return grupo.qs.map((q) => `${ano}Q${q}`)
    },

    anoTotalmenteSelecionado(ano) {
      const periodos = this.periodosDoAno(ano)

      return (
        periodos.length > 0 &&
        periodos.every((periodo) => this.periodosSelecionados.includes(periodo))
      )
    },

    garantirPeriodoSelecionado() {
      if (!this.serieVisivel.length) {
        this.periodoSelecionadoKey = ''
        return
      }

      const atual = this.normalizarChavePeriodo(this.quadrimestreAtual)
      const aindaVisivel = this.serieVisivel.some(
        (item) => item.quadrimestre === this.periodoSelecionadoKey
      )

      if (aindaVisivel) {
        return
      }

      const preferido = this.serieVisivel.find(
        (item) => item.quadrimestre === atual
      )

      this.periodoSelecionadoKey = preferido
        ? preferido.quadrimestre
        : this.serieVisivel[this.serieVisivel.length - 1].quadrimestre
    },

    toggleAnoCompleto(ano) {
      const periodos = this.periodosDoAno(ano)

      if (!periodos.length) {
        return
      }

      if (this.anoTotalmenteSelecionado(ano)) {
        const restante = this.periodosSelecionados.filter(
          (periodo) => !periodos.includes(periodo)
        )

        if (!restante.length) {
          return
        }

        this.periodosSelecionados = restante
        return
      }

      this.periodosSelecionados = [
        ...new Set([...this.periodosSelecionados, ...periodos]),
      ].sort((a, b) => this.compararPeriodos(a, b))
    },

    togglePeriodo(ano, q) {
      const chave = `${ano}Q${q}`

      if (this.periodosSelecionados.includes(chave)) {
        if (this.periodosSelecionados.length === 1) {
          return
        }

        this.periodosSelecionados = this.periodosSelecionados.filter(
          (periodo) => periodo !== chave
        )
        return
      }

      this.periodosSelecionados = [...this.periodosSelecionados, chave].sort(
        (a, b) => this.compararPeriodos(a, b)
      )
    },

    /**
     * Mesma regra da Experiência Exitosa / cartaz:
     * contagem (>0) | check (0 e faixas anteriores zeradas) | vazio (0 com faixa anterior ainda com equipes).
     *
     * @param {object} periodo Snapshot do período.
     * @param {number} index Índice Regular→Ótimo.
     * @returns {'contagem'|'check'|'vazio'}
     */
    situacaoFaixa(periodo, index) {
      if (!periodo) {
        return 'vazio'
      }

      const quantidade = Number(
        periodo[this.classificacoes[index].key] || 0
      )
      const anteriorComEquipe = this.classificacoes
        .slice(0, index)
        .some((faixa) => Number(periodo[faixa.key] || 0) > 0)

      if (quantidade === 0 && anteriorComEquipe) {
        return 'vazio'
      }

      if (quantidade === 0) {
        return 'check'
      }

      return 'contagem'
    },

    onChartMarkerClick(_event, _ctx, config) {
      this.selecionarPeriodoPorIndice(config?.dataPointIndex)
    },

    onChartAxisClick(_event, _ctx, config) {
      this.selecionarPeriodoPorIndice(config?.labelIndex)
    },

    /**
     * Clique na coluna vertical do Q
     *
     * @param {MouseEvent} event Evento do clique
     * @param {object} chartContext Contexto
     */
    onChartAreaClick(event, chartContext) {
      const index = this.indicePeriodoNoPlot(event, chartContext)
      this.selecionarPeriodoPorIndice(index)
    },

    onChartMouseMove(event) {
      this.chartHoverNoQ = this.indicePeriodoNoPlot(event) != null
    },

    onChartMouseLeave() {
      this.chartHoverNoQ = false
    },

    /**
     * Índice do Q sob o mouse — só perto da linha vertical de cada período.
     *
     * @param {MouseEvent} event Evento.
     * @param {object} [chartContext] Contexto Apex (opcional).
     * @returns {number|null}
     */
    indicePeriodoNoPlot(event, chartContext) {
      if (!event || !this.serieVisivel.length) {
        return null
      }

      const chart =
        (chartContext && chartContext.w && chartContext) ||
        (this.$refs.evolucaoChart && this.$refs.evolucaoChart.chart)

      const globals = chart && chart.w && chart.w.globals

      if (!globals || !globals.gridWidth || !globals.gridHeight) {
        return null
      }

      const baseEl =
        (globals.dom && globals.dom.baseEl) ||
        (chart && chart.el) ||
        null
      const svg = baseEl && baseEl.querySelector('svg')

      if (!svg) {
        return null
      }

      const rect = svg.getBoundingClientRect()
      const translateX = Number(globals.translateX || 0)
      const translateY = Number(globals.translateY || 0)
      const plotWidth = Number(globals.gridWidth)
      const plotHeight = Number(globals.gridHeight)
      const x = event.clientX - rect.left - translateX
      const y = event.clientY - rect.top - translateY

      if (x < 0 || x > plotWidth || y < 0 || y > plotHeight) {
        return null
      }

      const total = this.serieVisivel.length
      const centros = this.centrosXPeriodos(baseEl, plotWidth, total)

      // Corredor estreito em torno da linha do Q (não a coluna inteira)
      const espacamento =
        total > 1 ? plotWidth / (total - 1) : plotWidth
      const raio = Math.max(14, Math.min(28, espacamento * 0.28))

      let melhor = null
      let melhorDist = Infinity

      centros.forEach((cx, index) => {
        const dist = Math.abs(x - cx)

        if (dist <= raio && dist < melhorDist) {
          melhorDist = dist
          melhor = index
        }
      })

      return melhor
    },

    /**
     * Posições X (no plot) de cada Q — tenta ler dos markers; senão calcula.
     *
     * @param {Element|null} baseEl DOM do chart.
     * @param {number} plotWidth Largura do grid.
     * @param {number} total Qtde de períodos.
     * @returns {number[]}
     */
    centrosXPeriodos(baseEl, plotWidth, total) {
      const centros = []

      if (baseEl) {
        const markers = baseEl.querySelectorAll(
          '.apexcharts-series[rel="1"] .apexcharts-marker, .apexcharts-series:first-of-type .apexcharts-marker'
        )

        markers.forEach((marker) => {
          const cx = parseFloat(marker.getAttribute('cx'))
          const j = marker.getAttribute('j')
          const index =
            j != null && j !== ''
              ? Number(j)
              : centros.length

          if (Number.isFinite(cx) && Number.isFinite(index)) {
            centros[index] = cx
          }
        })
      }

      if (
        centros.filter((v) => Number.isFinite(v)).length === total
      ) {
        return centros
      }

      if (total <= 1) {
        return [plotWidth / 2]
      }

      return Array.from(
        { length: total },
        (_, index) => (index * plotWidth) / (total - 1)
      )
    },

    /**
     * Define o período selecionado pelo índice da série visível.
     *
     * @param {number|null|undefined} index Índice do quadrimestre.
     */
    selecionarPeriodoPorIndice(index) {
      if (index == null || index < 0) {
        return
      }

      const item = this.serieVisivel[index]

      if (item) {
        this.periodoSelecionadoKey = item.quadrimestre
      }
    },

    formatNumber(value) {
      return Number(value || 0).toLocaleString('pt-BR')
    },
  },
}
</script>

<style lang="scss" scoped>
.evolucao-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.6);
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.evolucao-panel {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 980px;
  max-height: 92vh;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(15, 23, 42, 0.3);
  display: flex;
  flex-direction: column;
}

.evolucao-header {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  padding: 18px 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;

  h3 {
    margin: 0;
    font-size: 18px;
    color: #1f2937;
    font-weight: 800;
    line-height: 1.3;
  }

  button {
    border: none;
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
    font-size: 18px;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    flex: 0 0 auto;
    transition: 0.2s;

    &:hover {
      color: #ef4444;
      background: #fef2f2;
    }
  }
}

.meta {
  margin: 8px 0 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #647a84;
}

.tipo-equipe-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #c6d7dc;
  background: #e8f4f6;
  color: #3b5966;
  border-radius: 9px;
  min-height: 28px;
  padding: 4px 11px;
  font-size: 12px;
  font-weight: 850;
  line-height: 1;
  flex: none;
}

.meta-titulo {
  color: #3b5966;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.35;
}

.evolucao-body {
  padding: 16px 20px 20px;
  overflow-y: auto;
}

.evolucao-state {
  text-align: center;
  color: #6b7280;
  padding: 48px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  &.compact {
    padding: 28px 12px;
  }

  i {
    font-size: 42px;
    color: #d1d5db;
  }

  strong {
    color: #374151;
    font-size: 17px;
  }

  span {
    font-size: 13px;
    max-width: 420px;
  }
}

.spin {
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.btn-retry {
  margin-top: 8px;
  border: 1px solid #c4d6dc;
  background: #fff;
  color: #1d4a5c;
  border-radius: 9px;
  min-height: 38px;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #edf8f8;
    border-color: #9abdc3;
  }
}

.filtros-periodo {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.filtro-ano {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
  border: 1px solid #e1ebed;
  background: #fafcfc;
  border-radius: 12px;
  padding: 8px 10px;
}

.filtro-ano-toggle {
  border: 1px solid #bdcfd3;
  background: #fff;
  color: #24495a;
  border-radius: 8px;
  min-height: 34px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 850;
  cursor: pointer;
  transition: 0.15s;

  &:hover {
    border-color: #338598;
    background: #f3fafb;
  }

  &.active {
    border-color: #16677b;
    background: #16677b;
    color: #fff;
  }
}

.filtro-opcoes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.filtro-chip {
  border: 1px solid #c6d7dc;
  background: #fff;
  color: #32525e;
  border-radius: 9px;
  min-height: 34px;
  min-width: 52px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: 0.15s;

  &:hover {
    border-color: #338598;
    background: #f3fafb;
  }

  &.active {
    border-color: #338598;
    background: #e8f4f6;
    color: #124b5c;
  }
}

.legend-swatch {
  width: 32px;
  height: 3px;
  border-radius: 99px;
  position: relative;
  display: inline-block;
  background: var(--swatch, currentColor);
  flex: none;
  margin: 6px auto 0;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 9px;
    height: 9px;
    background: var(--swatch, currentColor);
    border: 2px solid #fff;
    box-sizing: border-box;
    transform: translate(-50%, -50%);
  }

  &.shape-circle::after {
    border-radius: 50%;
  }

  &.shape-square::after {
    border-radius: 2px;
  }

  &.shape-diamond::after {
    width: 8px;
    height: 8px;
    border-radius: 1px;
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &.shape-triangle::after {
    width: 0;
    height: 0;
    border: 0;
    background: transparent;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 10px solid var(--swatch, currentColor);
    transform: translate(-50%, calc(-50% - 1px));
  }
}

.chart-wrap {
  border: 1px solid #e1ebee;
  border-radius: 12px;
  background: #fcfefe;
  padding: 8px 4px 0;
  margin-bottom: 14px;
  cursor: default;

  &.is-q-hover {
    cursor: pointer;

    ::v-deep .apexcharts-canvas,
    ::v-deep .apexcharts-svg,
    ::v-deep .apexcharts-inner,
    ::v-deep .apexcharts-graphical,
    ::v-deep .apexcharts-marker,
    ::v-deep .apexcharts-xaxis text {
      cursor: pointer !important;
    }
  }

  ::v-deep .apexcharts-tooltip {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    padding: 0 !important;
    pointer-events: none;
  }
}

.period-box {
  margin-top: 14px;
}

.stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.stat {
  min-width: 0;
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 112px;

  .cat {
    display: block;
    width: 100%;
    text-align: center;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 850;
  }

  .stat-body {
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 58px;
    margin-top: 8px;
  }

  .qty {
    display: block;
    font-size: clamp(26px, 3vw, 34px);
    font-weight: 820;
    letter-spacing: -1px;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .unit {
    display: block;
    font-size: 13px;
    font-weight: 600;
    margin-top: 4px;
    opacity: 0.85;
  }

  .stat-check {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 2.5px solid #16a34a;
    color: #16a34a;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;
  }

  &.reg {
    --swatch: #c2410c;
    background: #fff7ed;
    color: #c2410c;
    border-color: #fed7aa;
  }

  &.suf {
    --swatch: #a16207;
    background: #fefce8;
    color: #a16207;
    border-color: #fde68a;
  }

  &.bom {
    --swatch: #15803d;
    background: #f0fdf4;
    color: #15803d;
    border-color: #bbf7d0;
  }

  &.oti {
    --swatch: #1d4ed8;
    background: #eff6ff;
    color: #1d4ed8;
    border-color: #bfdbfe;
  }
}

@media (max-width: 720px) {
  .evolucao-overlay {
    align-items: flex-end;
    padding: 0;
  }

  .evolucao-panel {
    max-width: 100%;
    max-height: 94vh;
    border-radius: 18px 18px 0 0;
  }

  .evolucao-header,
  .evolucao-body {
    padding-left: 14px;
    padding-right: 14px;
  }

  .stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
